import config from '@/config.js';
import { event as gtagEvent } from 'vue-gtag';
import { v4 as uuidv4 } from 'uuid';

const SDK_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
const TIMEOUT_MS = 15000;
const VIEW_CONTEXTS = ['file', 'accesslink', 'embedded'];
const CAST_STATES = ['NO_DEVICES_AVAILABLE', 'NOT_CONNECTED', 'CONNECTING', 'CONNECTED'];
const SESSION_STATES = [
  'NO_SESSION', 'SESSION_STARTING', 'SESSION_STARTED', 'SESSION_START_FAILED',
  'SESSION_ENDING', 'SESSION_ENDED', 'SESSION_RESUMING', 'SESSION_RESUMED', 'SESSION_RESUME_FAILED',
];
const ERROR_CODES = [
  'sdk_unavailable', 'sdk_load_failed', 'connection_failed', 'media_load_failed',
  'remote_playback_failed', 'control_failed', 'return_local_blocked', 'unknown',
];

function identifier(value) {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0) return value;
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(value) ? value : null;
}

function sameIdentifier(first, second) {
  return identifier(first) !== null && identifier(second) !== null && String(first) === String(second);
}

function sameResource(first, second) {
  try {
    const a = new URL(first);
    const b = new URL(second);
    return a.protocol === 'https:' && b.protocol === 'https:'
      && !a.username && !a.password && !b.username && !b.password
      && a.origin === b.origin && a.pathname === b.pathname;
  } catch {
    return false;
  }
}

function matchesInfo(info, { podId, asset }) {
  return Boolean(info && asset && sameIdentifier(info.customData?.pod_id, podId)
    && sameIdentifier(info.customData?.asset_id, asset.id)
    && sameResource(info.contentId, asset.url));
}

function matchesSavedInfo(savedInfo, ownedInfo) {
  if (!ownedInfo || !matchesInfo(savedInfo, {
    podId: ownedInfo.customData?.pod_id,
    asset: { id: ownedInfo.customData?.asset_id, url: ownedInfo.contentId },
  })) return false;
  const savedCorrelation = savedInfo.customData?.playback_session_id;
  const ownedCorrelation = ownedInfo.customData?.playback_session_id;
  return savedCorrelation == null && ownedCorrelation == null
    ? true : sameIdentifier(savedCorrelation, ownedCorrelation);
}

function positive(value, fallback = 0) {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function position(value, duration) {
  const time = Number.isFinite(value) ? Math.max(0, value) : 0;
  return duration > 0 ? Math.min(time, duration) : time;
}

function isCancellation(error) {
  try {
    // Only inspect the SDK's enum, never stringify an error or its description.
    const code = typeof error === 'string' ? error : error?.code;
    return code === 'cancel' || code === 'CANCEL';
  } catch {
    return false;
  }
}

class CastSessionService {
  constructor() {
    this.listeners = new Set();
    this.context = null;
    this.player = null;
    this.controller = null;
    this.session = null;
    this.media = null;
    this.mediaAlive = false;
    this.pending = null;
    this.confirmedInfo = null;
    this.lastInfo = null;
    this.lastState = null;
    this.caption = null;
    this.confirmedTrackIds = null;
    this.correlation = null;
    this.association = null;
    this.activeSessionKey = null;
    this.rejectedInfos = new WeakSet();
    this.stopTimer = null;
    this.lastPickerCancelled = false;
    this.snapshot = {
      sdkState: 'loading', castState: 'NO_DEVICES_AVAILABLE', sessionState: 'NO_SESSION',
      connected: false, loading: false, mediaLoaded: false, podId: null, assetId: null,
      playbackSessionId: null, currentTime: 0, duration: 0, paused: true, volume: 1,
      muted: false, canPause: false, canSeek: false, canControlVolume: false,
      activeCaptionAssetId: null, errorCode: null, explicitReturn: false,
    };
    this.onCastState = (event) => this.guard(() => {
      if (CAST_STATES.includes(event.castState)) this.snapshot.castState = event.castState;
      this.refresh();
    });
    this.onSessionState = (event) => this.guard(() => this.sessionChanged(event));
    this.onRemoteChange = (event) => this.guard(() => {
      if (event?.fieldName === 'isMediaLoaded' && event.value === false) this.confirmedInfo = null;
      this.refresh();
    });
    this.onMediaSession = () => this.guard(() => this.refresh());
    this.onMediaUpdate = (alive) => this.guard(() => {
      this.mediaAlive = alive;
      this.confirmedTrackIds = null;
      this.refresh();
    });
  }

  guard(callback, code = 'control_failed') {
    try {
      return callback();
    } catch {
      this.fail(code);
      return undefined;
    }
  }

  subscribe(listener) {
    this.listeners.add(listener);
    this.deliver(listener);
    return () => this.listeners.delete(listener);
  }

  deliver(listener) {
    try {
      const result = listener({ ...this.snapshot });
      // Async subscriber errors must not reach global credential-bearing error instrumentation.
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch {
      // Subscribers are component-owned; a disposed subscriber cannot break the session owner.
    }
  }

  publish() {
    this.listeners.forEach((listener) => this.deliver(listener));
  }

  telemetry(name, lifecycleState, errorCode = null, association = this.association, correlation = this.correlation) {
    if (!import.meta.env.PROD) return;
    try {
      const payload = {
        lifecycle_state: lifecycleState,
        page_location: window.location.origin,
        page_referrer: window.location.origin,
        page_title: 'Google Cast',
      };
      if (identifier(correlation) !== null) payload.playback_session_id = correlation;
      if (association) {
        if (identifier(association.podId) !== null) payload.pod_id = association.podId;
        if (identifier(association.assetId) !== null) payload.asset_id = association.assetId;
        if (VIEW_CONTEXTS.includes(association.viewContext)) payload.view_context = association.viewContext;
      }
      if (ERROR_CODES.includes(errorCode)) payload.error_code = errorCode;
      const result = gtagEvent(name, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch {
      // Analytics is best-effort. No raw SDK error, title, URL or receiver name is recorded.
    }
  }

  fail(code) {
    const safeCode = ERROR_CODES.includes(code) ? code : 'unknown';
    if (this.snapshot.errorCode !== safeCode) {
      this.telemetry('cast_error', 'failed', safeCode);
    }
    this.snapshot.errorCode = safeCode;
    this.publish();
  }

  initialize() {
    const id = config.cast_receiver_application_id?.trim();
    if (!id || typeof window === 'undefined') {
      this.snapshot.sdkState = 'disabled';
      return Promise.resolve(this);
    }
    return new Promise((resolve) => {
      let settled = false;
      let timer;
      let script;
      const finish = (errorCode = null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (script) script.removeEventListener('error', onError);
        if (errorCode === 'sdk_unavailable') {
          // Unsupported SDK/browser environments are normal optional-feature absence.
          this.snapshot.sdkState = 'disabled';
          this.publish();
        } else if (errorCode) {
          this.snapshot.sdkState = 'failed';
          this.fail(errorCode);
        } else {
          this.snapshot.sdkState = 'ready';
          this.guard(() => this.refresh(), 'sdk_unavailable');
          this.publish();
        }
        resolve(this);
      };
      const available = (isAvailable) => {
        if (settled) return; // An initialization timeout cannot attach late listeners.
        if (!isAvailable) {
          finish('sdk_unavailable');
          return;
        }
        try {
          const framework = window.cast?.framework;
          const base = window.chrome?.cast;
          if (!framework || !base) {
            finish('sdk_unavailable');
            return;
          }
          this.context = framework.CastContext.getInstance();
          this.context.setOptions({
            receiverApplicationId: id,
            autoJoinPolicy: base.AutoJoinPolicy.ORIGIN_SCOPED,
          });
          this.player = new framework.RemotePlayer();
          this.controller = new framework.RemotePlayerController(this.player);
          this.context.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, this.onCastState);
          this.context.addEventListener(framework.CastContextEventType.SESSION_STATE_CHANGED, this.onSessionState);
          this.controller.addEventListener(framework.RemotePlayerEventType.ANY_CHANGE, this.onRemoteChange);
          const state = this.context.getCastState();
          if (CAST_STATES.includes(state)) this.snapshot.castState = state;
          this.bindSession(this.context.getCurrentSession());
          if (this.session) this.observeConnection('SESSION_RESUMED');
          finish();
        } catch {
          this.detachInitialization();
          finish('sdk_unavailable');
        }
      };
      const onError = () => finish('sdk_load_failed');
      try {
        window.__onGCastApiAvailable = available;
        timer = setTimeout(() => finish('sdk_unavailable'), TIMEOUT_MS);
        if (window.cast?.framework && window.chrome?.cast) {
          available(true);
          return;
        }
        script = Array.from(document.scripts).find((entry) => entry.src === SDK_URL);
        if (!script) {
          script = document.createElement('script');
          script.src = SDK_URL;
          script.async = true;
          script.addEventListener('error', onError);
          document.head.appendChild(script);
        } else {
          script.addEventListener('error', onError);
        }
      } catch {
        finish('sdk_load_failed');
      }
    });
  }

  detachInitialization() {
    // This only runs on partial SDK initialization failure, not route unmount.
    try {
      const framework = window.cast?.framework;
      this.context?.removeEventListener(framework?.CastContextEventType.CAST_STATE_CHANGED, this.onCastState);
      this.context?.removeEventListener(framework?.CastContextEventType.SESSION_STATE_CHANGED, this.onSessionState);
      this.controller?.removeEventListener(framework?.RemotePlayerEventType.ANY_CHANGE, this.onRemoteChange);
      this.bindSession(null);
    } catch {
      // A broken SDK must not leak its exceptions into global handlers.
    }
    this.context = null;
    this.player = null;
    this.controller = null;
  }

  bindSession(session) {
    if (session === this.session) return;
    const eventType = window.cast.framework.SessionEventType.MEDIA_SESSION;
    this.session?.removeEventListener(eventType, this.onMediaSession);
    this.bindMedia(null);
    this.session = session;
    this.session?.addEventListener(eventType, this.onMediaSession);
  }

  bindMedia(media) {
    if (media === this.media) return;
    this.media?.removeUpdateListener(this.onMediaUpdate);
    this.media = media;
    this.confirmedTrackIds = null;
    this.mediaAlive = Boolean(media);
    this.media?.addUpdateListener(this.onMediaUpdate);
  }

  observeConnection(state) {
    const key = this.session?.getSessionId();
    if (!key) return;
    const newConnection = this.activeSessionKey !== key;
    if (newConnection) {
      const savedCorrelation = this.session.getMediaSession()?.media?.customData?.playback_session_id;
      this.correlation = typeof savedCorrelation === 'string'
        && /^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(savedCorrelation)
        ? savedCorrelation : uuidv4();
      this.association = null;
      this.activeSessionKey = key;
      this.snapshot.explicitReturn = false;
    }
    this.snapshot.sessionState = state;
    if (newConnection) {
      this.telemetry(state === 'SESSION_RESUMED' ? 'cast_session_resume' : 'cast_session_start',
        state === 'SESSION_RESUMED' ? 'resumed' : 'connected');
    }
  }

  sessionChanged(event) {
    const state = event.sessionState;
    if (!SESSION_STATES.includes(state)) return;
    this.snapshot.sessionState = state;
    if (state === 'SESSION_ENDED') {
      this.disconnected();
      return;
    }
    this.bindSession(this.context.getCurrentSession());
    if (state === 'SESSION_STARTED' || state === 'SESSION_RESUMED') this.observeConnection(state);
    if ((state === 'SESSION_START_FAILED' || state === 'SESSION_RESUME_FAILED') && !this.pending
      && !(state === 'SESSION_START_FAILED' && this.lastPickerCancelled)
      && !isCancellation(event.errorCode)) {
      this.fail('connection_failed');
    }
    this.refresh();
  }

  currentInfo() {
    return this.media?.media || this.player?.mediaInfo || this.confirmedInfo;
  }

  usableInfo(info) {
    return Boolean(info && !this.rejectedInfos.has(info));
  }

  rejectHandoff(intent, succeeded = false) {
    if (!intent?.loadSent) return;
    this.guard(() => {
      if (this.context.getCurrentSession() !== intent.session) return;
      const media = this.context.getCurrentSession()?.getMediaSession();
      const info = media?.media || this.player?.mediaInfo;
      const changed = info !== intent.previousInfo || media?.mediaSessionId !== intent.previousMediaId
        || !matchesInfo(info, intent.previousContext);
      if ((succeeded || changed) && matchesInfo(info, intent)) this.rejectedInfos.add(info);
    });
  }

  refresh() {
    if (!this.context || !this.player) return;
    const session = this.context.getCurrentSession();
    if (!session) {
      if (this.snapshot.connected || this.activeSessionKey) this.disconnected();
      else this.publish(); // Discovery changes must reach the UI before any session is started.
      return;
    }
    this.bindSession(session);
    this.bindMedia(session.getMediaSession());
    this.snapshot.connected = true;
    const info = this.currentInfo();
    const loaded = Boolean(!this.pending && this.usableInfo(info)
      && (this.media ? this.mediaAlive && this.media.playerState !== 'IDLE'
        : this.player.isMediaLoaded || this.confirmedInfo));
    this.snapshot.mediaLoaded = loaded;
    this.snapshot.podId = identifier(info?.customData?.pod_id);
    this.snapshot.assetId = identifier(info?.customData?.asset_id);
    this.snapshot.playbackSessionId = identifier(info?.customData?.playback_session_id) || this.correlation;
    this.snapshot.volume = Number.isFinite(this.player.volumeLevel)
      ? Math.min(1, Math.max(0, this.player.volumeLevel)) : this.snapshot.volume;
    this.snapshot.muted = Boolean(this.player.isMuted);
    this.snapshot.canPause = loaded && Boolean(this.player.canPause);
    this.snapshot.canSeek = loaded && Boolean(this.player.canSeek);
    this.snapshot.canControlVolume = Boolean(this.player.canControlVolume);
    if (loaded) {
      this.snapshot.duration = positive(this.player.duration, positive(info?.duration, this.snapshot.duration));
      if (this.player.isMediaLoaded) {
        this.snapshot.currentTime = position(this.player.currentTime, this.snapshot.duration);
        this.snapshot.paused = Boolean(this.player.isPaused);
      }
      this.lastInfo = info;
      this.lastState = {
        currentTime: this.snapshot.currentTime, duration: this.snapshot.duration, paused: this.snapshot.paused,
      };
      this.snapshot.activeCaptionAssetId = this.getCaptionState({
        assets: this.caption ? [this.caption] : [],
      }).activeCaptionAssetId;
    }
    if (this.media?.idleReason === 'ERROR') {
      this.confirmedInfo = null;
      this.snapshot.mediaLoaded = false;
      this.fail('remote_playback_failed');
    } else if (this.media?.playerState === 'IDLE') {
      this.confirmedInfo = null;
      this.snapshot.mediaLoaded = false;
    }
    if (!this.snapshot.mediaLoaded) {
      this.snapshot.canPause = false;
      this.snapshot.canSeek = false;
    }
    this.publish();
  }

  disconnected() {
    const saved = this.player?.savedPlayerState;
    if (saved && matchesSavedInfo(saved.mediaInfo, this.lastInfo)) {
      this.lastState = {
        currentTime: position(saved.currentTime, this.snapshot.duration),
        paused: Boolean(saved.isPaused), duration: this.snapshot.duration,
      };
    }
    if (this.lastState) Object.assign(this.snapshot, this.lastState);
    clearTimeout(this.stopTimer);
    this.stopTimer = null;
    if (this.activeSessionKey) this.telemetry('cast_session_stop', 'ended');
    this.activeSessionKey = null;
    this.pending = null;
    this.confirmedInfo = null;
    this.snapshot.connected = false;
    this.snapshot.loading = false;
    this.snapshot.mediaLoaded = false;
    this.snapshot.canPause = false;
    this.snapshot.canSeek = false;
    this.snapshot.canControlVolume = false;
    this.snapshot.sessionState = 'SESSION_ENDED';
    this.snapshot.castState = 'NOT_CONNECTED';
    this.bindSession(null);
    this.publish();
  }

  // These read-only APIs keep signed receiver URLs and SDK objects outside Vue reactivity.
  matchesMedia(context) {
    return this.guard(() => this.snapshot.connected && this.snapshot.mediaLoaded
      && this.usableInfo(this.currentInfo()) && matchesInfo(this.currentInfo(), context)) === true;
  }

  getReturnState(context) {
    return this.guard(() => {
      const saved = this.player?.savedPlayerState;
      if (!this.snapshot.connected && saved && matchesSavedInfo(saved.mediaInfo, this.lastInfo)
        && matchesInfo(saved.mediaInfo, context)) {
        const duration = positive(saved.mediaInfo?.duration, this.snapshot.duration);
        return {
          currentTime: position(saved.currentTime, duration), paused: Boolean(saved.isPaused),
          duration, explicitReturn: this.snapshot.explicitReturn,
        };
      }
      if (!this.lastState || !matchesInfo(this.lastInfo, context)) return null;
      return { ...this.lastState, explicitReturn: this.snapshot.explicitReturn };
    }) || null;
  }

  getCaptionState({ assets = [] } = {}) {
    return this.guard(() => {
      const textTracks = this.currentInfo()?.tracks?.filter((entry) => entry.type === 'TEXT') || [];
      const track = textTracks.find((entry) => entry.trackId === 1);
      const asset = track && assets.find((entry) => sameResource(track.trackContentId, entry.url));
      const loadedCaptionAssetId = identifier(asset?.id);
      const activeIds = this.confirmedTrackIds || this.media?.activeTrackIds || [];
      const hasUnsupportedCaption = (textTracks.length > 0 && loadedCaptionAssetId === null)
        || textTracks.some((entry) => entry.trackId !== 1 && activeIds.includes(entry.trackId));
      return {
        loadedCaptionAssetId,
        activeCaptionAssetId: activeIds.includes(1) ? loadedCaptionAssetId : null,
        hasUnsupportedCaption,
      };
    }) || { loadedCaptionAssetId: null, activeCaptionAssetId: null, hasUnsupportedCaption: true };
  }

  async start({ podId, asset, caption = null, title, posterUrl, viewContext, getLocalState, validate }) {
    if (this.snapshot.sdkState !== 'ready' || this.pending || this.stopTimer) return;
    let intent;
    let requestedSession;
    try {
      // Capture identities and initial position before opening the picker. Never retain a mutable DTO.
      intent = {
        podId, asset: { ...asset }, caption: caption ? { ...caption } : null,
        initialState: { ...getLocalState() }, playbackId: uuidv4(),
      };
      if (!validate()) return;
      this.pending = intent;
      this.lastPickerCancelled = false;
      this.snapshot.loading = true;
      this.snapshot.errorCode = null;
      this.snapshot.explicitReturn = false;
      this.publish();
      // This call is deliberately before the first await to preserve the browser user gesture.
      requestedSession = this.context.requestSession();
      await requestedSession;
    } catch (error) {
      if (this.pending === intent) {
        this.pending = null;
        this.snapshot.loading = false;
        this.lastPickerCancelled = isCancellation(error);
        if (!this.lastPickerCancelled) this.fail('connection_failed');
        this.guard(() => this.refresh());
        this.publish();
      }
      return;
    }
    try {
      if (this.pending !== intent || !validate()) return;
      const session = this.context.getCurrentSession();
      if (!session) {
        this.fail('connection_failed');
        return;
      }
      this.bindSession(session);
      if (!this.activeSessionKey) this.observeConnection('SESSION_STARTED');
      const localState = getLocalState();
      const duration = positive(localState.duration, positive(intent.asset.duration));
      const base = window.chrome.cast;
      const info = new base.media.MediaInfo(intent.asset.url, intent.asset.mimetype);
      info.streamType = base.media.StreamType.BUFFERED;
      info.duration = duration;
      info.metadata = new base.media.GenericMediaMetadata();
      info.metadata.title = title || '';
      if (posterUrl) {
        try {
          const poster = new URL(posterUrl);
          if (poster.protocol === 'https:' && !poster.username && !poster.password) {
            info.metadata.images = [new base.Image(posterUrl)];
          }
        } catch {
          // Poster failure must not prevent the authorized media load.
        }
      }
      info.customData = {
        pod_id: intent.podId, asset_id: intent.asset.id, playback_session_id: intent.playbackId,
        view_context: VIEW_CONTEXTS.includes(viewContext) ? viewContext : 'file',
      };
      const request = new base.media.LoadRequest(info);
      request.currentTime = position(localState.currentTime, duration);
      request.autoplay = !localState.paused;
      if (intent.caption) {
        const track = new base.media.Track(1, base.media.TrackType.TEXT);
        track.subtype = base.media.TextTrackType.CAPTIONS;
        track.trackContentId = intent.caption.url;
        track.trackContentType = 'text/vtt';
        track.name = intent.caption.label || intent.caption.name || '';
        track.language = intent.caption.language || '';
        info.tracks = [track];
        request.activeTrackIds = [1];
      }
      const association = {
        podId: identifier(intent.podId), assetId: identifier(intent.asset.id),
        viewContext: info.customData.view_context,
      };
      this.association = association;
      // A connection and its subsequent media load share one sender-generated correlation.
      info.customData.playback_session_id = this.correlation || intent.playbackId;
      intent.playbackId = info.customData.playback_session_id;
      // Re-evaluate expiry/context as close as possible to sending the load request.
      if (!validate()) return;
      const previousMedia = session.getMediaSession();
      intent.previousInfo = previousMedia?.media || this.player?.mediaInfo;
      intent.previousContext = {
        podId: intent.previousInfo?.customData?.pod_id,
        asset: { id: intent.previousInfo?.customData?.asset_id, url: intent.previousInfo?.contentId },
      };
      intent.previousMediaId = previousMedia?.mediaSessionId;
      intent.session = session;
      intent.loadSent = true;
      await session.loadMedia(request);
      this.telemetry('cast_media_load', 'loaded', null, association, intent.playbackId);
      if (this.pending !== intent || this.context.getCurrentSession() !== session || !validate()) {
        this.rejectHandoff(intent, true);
        return;
      }
      this.pending = null;
      const currentMedia = session.getMediaSession();
      if (currentMedia?.media) this.rejectedInfos.delete(currentMedia.media);
      if (this.player?.mediaInfo && matchesInfo(this.player.mediaInfo, intent)) {
        this.rejectedInfos.delete(this.player.mediaInfo);
      }
      this.confirmedInfo = info;
      this.caption = intent.caption;
      this.snapshot.loading = false;
      this.snapshot.errorCode = null;
      this.snapshot.mediaLoaded = true;
      this.snapshot.duration = duration;
      this.snapshot.currentTime = request.currentTime;
      this.snapshot.paused = !request.autoplay;
      this.lastInfo = info;
      this.lastState = {
        currentTime: request.currentTime, duration, paused: !request.autoplay,
      };
      this.refresh();
    } catch {
      this.rejectHandoff(intent);
      if (this.pending === intent) this.fail('media_load_failed');
    } finally {
      if (this.pending === intent) {
        this.pending = null;
        this.snapshot.loading = false;
        this.guard(() => this.refresh());
        this.publish();
      }
    }
  }

  reportReturnLocalBlocked() {
    this.fail('return_local_blocked');
  }

  command(callback, capability) {
    this.guard(() => {
      if (!this.snapshot.connected || !this.snapshot.mediaLoaded || this.pending || this.stopTimer) return;
      if (capability && !this.player[capability]) return;
      const result = callback();
      if (result && typeof result.catch === 'function') result.catch(() => this.fail('control_failed'));
    });
  }

  play() {
    this.command(() => {
      if (this.player.isPaused) return this.controller.playOrPause();
      return undefined;
    }, 'canPause');
  }

  pause() {
    this.command(() => {
      if (!this.player.isPaused) return this.controller.playOrPause();
      return undefined;
    }, 'canPause');
  }

  togglePlay() {
    this.command(() => this.controller.playOrPause(), 'canPause');
  }

  seek(seconds) {
    this.command(() => {
      if (!Number.isFinite(seconds)) return;
      this.player.currentTime = position(seconds, this.snapshot.duration);
      return this.controller.seek();
    }, 'canSeek');
  }

  setVolume(level) {
    this.command(() => {
      if (!Number.isFinite(level)) return;
      this.player.volumeLevel = Math.min(1, Math.max(0, level));
      return this.controller.setVolumeLevel();
    }, 'canControlVolume');
  }

  setMuted(muted) {
    this.command(() => {
      if (this.player.isMuted !== Boolean(muted)) return this.controller.muteOrUnmute();
      return undefined;
    }, 'canControlVolume');
  }

  selectCaption(enabled) {
    return new Promise((resolve) => {
      try {
        if (!this.snapshot.connected || !this.snapshot.mediaLoaded || this.pending || this.stopTimer) {
          resolve();
          return;
        }
        const media = this.media;
        const track = media?.media?.tracks?.find((entry) => entry.trackId === 1 && entry.type === 'TEXT');
        if (!media || (enabled && !track)) {
          this.fail('control_failed');
          resolve();
          return;
        }
        const ids = enabled ? [1] : [];
        const request = new window.chrome.cast.media.EditTracksInfoRequest(ids);
        media.editTracksInfo(request, () => {
          this.guard(() => {
            if (media === this.media && this.snapshot.connected) {
              // Success confirms selection even if the next media-status event has not arrived yet.
              this.confirmedTrackIds = ids;
              this.snapshot.activeCaptionAssetId = enabled ? identifier(this.caption?.id) : null;
              this.snapshot.errorCode = null;
              this.publish();
            }
          });
          resolve();
        }, () => {
          this.guard(() => {
            if (media === this.media) this.fail('control_failed');
          });
          resolve();
        });
      } catch {
        this.fail('control_failed');
        resolve();
      }
    });
  }

  stopCasting() {
    this.guard(() => {
      if (!this.session || !this.snapshot.connected || this.stopTimer) return;
      this.refresh();
      this.snapshot.explicitReturn = true;
      this.snapshot.errorCode = null;
      this.publish();
      const session = this.session;
      const stopFailed = () => {
        if (this.session !== session || !this.snapshot.connected) return;
        clearTimeout(this.stopTimer);
        this.stopTimer = null;
        this.snapshot.explicitReturn = false;
        this.fail('control_failed');
      };
      // endSession has a void API. A timeout reports an unconfirmed stop, never a local return.
      this.stopTimer = setTimeout(() => this.guard(stopFailed), TIMEOUT_MS);
      try {
        const result = session.endSession(true);
        if (result && typeof result.catch === 'function') result.catch(() => this.guard(stopFailed));
      } catch {
        stopFailed();
      }
    });
  }
}

let singletonPromise;

export function getGoogleCastSession() {
  if (!singletonPromise) {
    const service = new CastSessionService();
    singletonPromise = service.initialize().catch(() => {
      service.snapshot.sdkState = 'failed';
      service.fail('sdk_unavailable');
      return service;
    });
  }
  return singletonPromise;
}
